import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { submitLead } from "../lib/api";
import { track, trackLead } from "../lib/analytics";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const INTEREST_KEYS = ["general", "valuation", "buy", "sell", "rent", "rent_mid", "manage", "concierge"];
const LEAD_TYPE = { manage: "owner", concierge: "owner", valuation: "owner", sell: "owner", buy: "buyer", rent: "renter", rent_mid: "renter", general: "general" };

export const ContactForm = ({ propertyId = null, defaultInterest = "general", compact = false }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: defaultInterest, message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ ...form, property_id: propertyId });
      const leadType = LEAD_TYPE[form.interest] || "general";
      track("contact_form_submit", { interest: form.interest, lead_type: leadType, has_property: Boolean(propertyId) });
      trackLead(leadType, propertyId ? "property_inquiry" : "contact_form", { interest: form.interest });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", interest: defaultInterest, message: "" });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-border/60 focus:border-primary focus:outline-none rounded-none px-0 py-3 font-body text-foreground placeholder:text-muted-foreground/70 transition-colors duration-300";

  return (
    <form onSubmit={handleSubmit} data-testid="contact-form" className="flex flex-col gap-8">
      <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-8`}>
        <div className="flex flex-col gap-2">
          <label className="font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t("contact.name")}</label>
          <input data-testid="contact-name" id="contact-name" name="name" autoComplete="name" required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputClass} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t("contact.email")}</label>
          <input data-testid="contact-email" id="contact-email" name="email" type="email" autoComplete="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputClass} />
        </div>
      </div>

      <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-8`}>
        <div className="flex flex-col gap-2">
          <label className="font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t("contact.phone")}</label>
          <input data-testid="contact-phone" id="contact-phone" name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputClass} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t("contact.interest")}</label>
          <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
            <SelectTrigger data-testid="contact-interest" className="w-full bg-transparent border-0 border-b border-border/60 rounded-none px-0 py-3 h-auto font-body text-foreground focus:ring-0 focus:border-primary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              {INTEREST_KEYS.map((k) => (
                <SelectItem key={k} value={k} data-testid={`interest-${k}`} className="font-body">
                  {t(`contact.interestOptions.${k}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t("contact.message")}</label>
        <textarea data-testid="contact-message" id="contact-message" name="message" required rows={compact ? 3 : 4} value={form.message} onChange={(e) => update("message", e.target.value)} className={`${inputClass} resize-none`} />
      </div>

      <button
        type="submit"
        data-testid="contact-submit"
        disabled={loading}
        className="self-start inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300"
      >
        {loading ? <><Loader2 size={15} className="animate-spin" /> {t("contact.sending")}</> : t("contact.submit")}
      </button>
    </form>
  );
};

export default ContactForm;
